import React, { useEffect, useState } from 'react';
// Popconfirm: only used by the commented-out Delete action below (see "Action" column) —
// re-add it here if uncommenting that.
import { Button, Input, Select, InputNumber, Tooltip, Row, Col, Tag, Modal, Table as AntTable } from 'antd';
import { toast } from 'react-toastify';
import Icons from '@/components/third-party/Icons';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import TableContainer from '@/components/TableContainer';
import {
  get_recipe_variant_list,
  add_recipe_variant,
  update_recipe_variant,
  // delete_recipe_variant, // only used by the commented-out Delete action below — uncomment both to restore
  get_variant_type_list,
  get_recipe_meta_form,
  get_output_form_type_by_id,
  get_recipe_bom_variant_breakdown,
} from '@/utils/api';
import { bp_units_data } from '@/utils/api';

const RULE_TYPE_LABELS = {
  PER_UNIT: 'Per Unit',
  PER_VOLUME: 'Per Volume',
  PER_MASS: 'Per Mass',
  PERCENT_WW: '% w/w',
  PERCENT_WV: '% w/v',
  PERCENT_VV: '% v/v',
  FIXED_BATCH_QTY: 'Fixed',
  QS_FINAL_VOLUME: 'QS to Final Volume',
  QS_FINAL_WEIGHT: 'QS to Final Weight',
  QS_TARGET_PARAMETER: 'QS to Target Parameter',
  QS_PROCESS: 'QS as Required',
  FIXED_COUNT: 'Count',
};

// Std/Corrected Qty on a BOM item is always per-batch — dividing by a
// variant's output_quantity gives a per-single-unit value, usually a tiny
// fraction of the batch-level number, so it needs more decimal places than
// the recipe's own configured precision to not just show "0.000".
const formatPerUnit = (v) => {
  if (v === null || v === undefined) return '—';
  return Number(v).toFixed(6).replace(/\.?0+$/, '') || '0';
};

// Value's meaning depends on what kind of UOM it's measured in — mirrors the
// same WEIGHT/VOLUME/COUNT bucketing bp_unit.unit_category already provides.
const CATEGORY_VALUE_LABEL = { WEIGHT: 'Weight per Unit', VOLUME: 'Volume per Unit', COUNT: 'Nos per Unit' };

// 2026-09-02, "labels?" — the Add/Edit Variant form used bare `placeholder=` text as each
// field's only label; once a value was picked/typed the placeholder disappears and there's
// nothing left saying what the field is (worst for the Value field, whose whole purpose —
// Weight/Volume/Nos per Unit — was then invisible). A real caption above each control instead.
const FieldLabel = ({ children, textColor }) => (
  <div style={{ fontSize: 12, marginBottom: 4, opacity: 0.65, color: textColor }}>{children}</div>
);

const emptyForm = {
  variant_id: null,
  variant_type_id: null,
  name: '',
  value: '',
  uom_id: null,
  output_quantity: '',
  output_uom_id: null,
};

// Recipe variants (e.g. 10mg / 20mg strengths, 2mL / 5mL fill sizes) — each
// computes how many output units (tablets/capsules/vials) the recipe's batch
// size yields at that variant's value. output_quantity defaults from
// recipe.master_quantity / value server-side but is always user-overridable.
// hideAddVariant: 2026-09-01, "cannot add variants here anymore — it's fixed based on what was
// selected from product and variant selection" — an MFR's content_recipe has its Recipe_Variant
// rows auto-mapped from the product's own Product_Variant hierarchy (mfr_view._sync_recipe_variants,
// see BMR_INGESTION_PLAN.md); manually adding another one here would drift out of sync with that
// source of truth. Edit (to set Output Quantity) and Delete stay available — only Add is hidden.
export default function RecipeVariantsPanel({ recipeId, hideAddVariant }) {
  const { isDark } = useTheme();
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const formBgColor = isDark ? defaultColors.palette.gray[700] : '#f8faff';
  const accentColor = '#3b82f6';
  const accentBg = isDark ? 'rgba(59,130,246,0.08)' : '#eff6ff';

  const [variants, setVariants] = useState([]);
  const [variantTypes, setVariantTypes] = useState([]);
  const [units, setUnits] = useState([]);
  // This recipe's Output Form unit categories (WEIGHT/VOLUME/COUNT) — restricts
  // the Value UOM picker below, same constraint used for BOM Rule Type UOM.
  const [outputUnitCategories, setOutputUnitCategories] = useState([]);
  const [recipeUnitId, setRecipeUnitId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // "Composition per Unit" viewer — pick a variant (via its row's view icon)
  // and see the BOM's per-batch quantities divided down to a single output
  // unit of that variant, the standard per-tablet/capsule/vial companion to
  // the per-batch BOM table.
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerVariantId, setViewerVariantId] = useState(null);
  const [viewerVariant, setViewerVariant] = useState(null);
  const [viewerRows, setViewerRows] = useState([]);
  const [viewerRecipeUnitName, setViewerRecipeUnitName] = useState('');
  const [viewerDisplayUnitName, setViewerDisplayUnitName] = useState('');
  // Per-unit numbers default to the recipe's own unit (often too coarse —
  // e.g. "0.005 kg") — this lets the user re-express them in a smaller,
  // same-category unit (e.g. mg) for a readable number.
  const [displayUnitId, setDisplayUnitId] = useState(null);

  const fetchBreakdown = (variantId, displayUnitIdParam) => {
    setViewerLoading(true);
    get_recipe_bom_variant_breakdown((res) => {
      setViewerLoading(false);
      if (res?.data?.status === 'success') {
        setViewerVariant(res.data.variant || null);
        setViewerRows(res.data.data || []);
        setViewerRecipeUnitName(res.data.recipe_unit_name || '');
        setViewerDisplayUnitName(res.data.display_unit_name || res.data.recipe_unit_name || '');
      } else {
        toast.warning(res?.data?.message || 'Failed to load BOM breakdown');
      }
    }, { recipe_id: recipeId, variant_id: variantId, display_unit_id: displayUnitIdParam || undefined });
  };

  const handleView = (record) => {
    setViewerOpen(true);
    setViewerVariantId(record.variant_id);
    setViewerVariant(null);
    setViewerRows([]);
    setDisplayUnitId(recipeUnitId);
    fetchBreakdown(record.variant_id, recipeUnitId);
  };

  const handleDisplayUnitChange = (val) => {
    setDisplayUnitId(val);
    fetchBreakdown(viewerVariantId, val);
  };

  const fetchVariants = () => {
    if (!recipeId) return;
    get_recipe_variant_list((res) => {
      if (res?.data?.status === 'success') setVariants(res?.data?.data || []);
    }, { recipe_id: recipeId });
  };

  useEffect(() => {
    fetchVariants();
    get_variant_type_list((res) => {
      if (res?.data?.status === 'success') setVariantTypes(res?.data?.data || []);
    });
    bp_units_data((res) => {
      if (res?.data?.status === 'success') setUnits(res?.data?.data || []);
    });
    if (recipeId) {
      get_recipe_meta_form((res) => {
        setRecipeUnitId(res?.data?.data?.unit || null);
        const outputFormTypeId = res?.data?.data?.output_form_id;
        if (res?.data?.status === 'success' && outputFormTypeId) {
          get_output_form_type_by_id((oftRes) => {
            if (oftRes?.data?.status === 'success') {
              setOutputUnitCategories(oftRes?.data?.unit_categories || []);
            }
          }, { output_form_type_id: outputFormTypeId });
        } else {
          setOutputUnitCategories([]);
        }
      }, { recipe_id: recipeId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeId]);

  const resetForm = () => {
    setShowForm(false);
    setForm(emptyForm);
  };

  // 2026-09-02: Add/Edit/Delete hidden from the Variants table (view-only for now) — the
  // handlers and their APIs still work, just commented out rather than deleted, in case
  // this needs to come back.
  // const handleEdit = (record) => {
  //   setForm({
  //     variant_id: record.variant_id,
  //     variant_type_id: record.variant_type_id,
  //     name: record.name || '',
  //     value: record.value,
  //     uom_id: record.uom_id,
  //     output_quantity: record.output_quantity ?? '',
  //     output_uom_id: record.output_uom_id,
  //   });
  //   setShowForm(true);
  // };

  const handleSave = () => {
    if (!form.variant_type_id || !form.value || !form.uom_id || !form.output_uom_id) {
      toast.warning('Variant type, value, UOM and output UOM are required');
      return;
    }
    setSaving(true);
    const payload = {
      variant_type_id: form.variant_type_id,
      name: form.name || '',
      value: Number(form.value),
      uom_id: form.uom_id,
      output_uom_id: form.output_uom_id,
      // Blank output_quantity -> let the backend auto-calc from batch size.
      output_quantity: form.output_quantity === '' ? null : Number(form.output_quantity),
    };
    const onOk = (res) => {
      setSaving(false);
      if (res?.status === 'success' || res?.data?.status === 'success') {
        toast.success(res?.message || res?.data?.message || 'Variant saved');
        fetchVariants();
        resetForm();
      } else {
        toast.warning(res?.message || res?.data?.message || 'Failed to save variant');
      }
    };
    if (form.variant_id) {
      update_recipe_variant(onOk, { ...payload, variant_id: form.variant_id });
    } else {
      add_recipe_variant(onOk, { ...payload, recipe_id: recipeId });
    }
  };

  // const handleDelete = (record) => {
  //   delete_recipe_variant((res) => {
  //     if (res?.status === 'success' || res?.data?.status === 'success') {
  //       toast.success('Variant deleted');
  //       fetchVariants();
  //     } else {
  //       toast.warning(res?.message || res?.data?.message || 'Failed to delete variant');
  //     }
  //   }, { variant_id: record.variant_id });
  // };

  const unitOptions = units.map((u) => ({ label: u.unit_name, value: u.id }));
  // Value UOM (strength/fill-size unit) restricted to the recipe's Output
  // Form categories — e.g. a Tablets (WEIGHT) recipe only offers mg/g/kg here.
  // Output UOM (the physical output-item count, e.g. tablets/capsules) is
  // intentionally left unrestricted.
  const valueUomOptions = outputUnitCategories.length
    ? units.filter((u) => outputUnitCategories.includes(u.unit_category)).map((u) => ({ label: u.unit_name, value: u.id }))
    : unitOptions;
  // What the Value field/column currently represents — Weight/Volume/Nos
  // (per Unit) based on the recipe's Output Form category. A recipe's
  // variants are always measured in units from that same restricted set (see
  // valueUomOptions above), so one label covers the whole column — generic
  // "Value" when the Output Form is unset/unrestricted (mixed categories
  // possible) or until a UOM is picked in the form.
  const singleValueCategory = outputUnitCategories.length === 1 ? outputUnitCategories[0] : null;
  const selectedValueCategory = units.find((u) => u.id === form.uom_id)?.unit_category;
  const valueFieldLabel = CATEGORY_VALUE_LABEL[selectedValueCategory || singleValueCategory] || 'Value';
  const valueColumnTitle = (singleValueCategory && CATEGORY_VALUE_LABEL[singleValueCategory]) || 'Value';

  const columns = [
    {
      title: 'Label',
      dataIndex: 'name',
      key: 'name',
      render: (v, r) => v || `${r.value} ${r.uom_name || ''}`,
    },
    {
      title: 'Type',
      dataIndex: 'variant_type_name',
      key: 'variant_type_name',
      render: (v) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: valueColumnTitle,
      key: 'value',
      render: (_, r) => `${r.value} ${r.uom_name || ''}`,
    },
    {
      title: 'Output Quantity',
      key: 'output',
      render: (_, r) => `${r.output_quantity ?? '-'} ${r.output_uom_name || ''}`,
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <div className="flex items-center justify-center gap-2">
          <Tooltip title="View Composition per Unit">
            <Icons.Eye onClick={() => handleView(record)} className="text-blue-600 w-4 h-4 cursor-pointer hover:text-blue-800" />
          </Tooltip>
          {/* 2026-09-02: Edit/Delete hidden (view-only for now) — handlers commented out
              above, APIs still work, uncomment to restore.
          <Tooltip title="Edit">
            <Icons.Pencil onClick={() => handleEdit(record)} className="text-amber-600 w-4 h-4 cursor-pointer hover:text-amber-800" />
          </Tooltip>
          <Popconfirm title="Delete this variant?" onConfirm={() => handleDelete(record)} okText="Yes" cancelText="No">
            <Tooltip title="Delete">
              <Icons.Trash2 className="text-red-600 w-4 h-4 cursor-pointer hover:text-red-800" />
            </Tooltip>
          </Popconfirm>
          */}
        </div>
      ),
    },
  ];

  const viewerColumns = [
    {
      title: 'Material',
      dataIndex: 'material_name',
      key: 'material_name',
      render: (v, r) => (
        <span>
          {v}
          {r.alias && <span className="text-gray-400"> ({r.alias})</span>}
        </span>
      ),
    },
    {
      title: 'Rule',
      dataIndex: 'rule_type',
      key: 'rule_type',
      render: (v) => (v ? <Tag color="blue">{RULE_TYPE_LABELS[v] || v}</Tag> : <span className="text-gray-400">—</span>),
    },
    {
      title: `Qty / Batch (${viewerRecipeUnitName})`,
      key: 'computed_quantity',
      align: 'right',
      render: (_, r) => (r.computed_quantity != null ? r.computed_quantity.toFixed(3) : '—'),
    },
    {
      title: `Qty / Unit (${viewerDisplayUnitName})`,
      key: 'qty_per_unit',
      align: 'right',
      render: (_, r) => <span className="font-medium">{formatPerUnit(r.qty_per_unit)}</span>,
    },
    {
      title: `Corrected Qty / Unit (${viewerDisplayUnitName})`,
      key: 'corrected_qty_per_unit',
      align: 'right',
      render: (_, r) => formatPerUnit(r.corrected_qty_per_unit),
    },
  ];

  const recipeUnitCategory = units.find((u) => u.id === recipeUnitId)?.unit_category;
  const displayUnitOptions = (recipeUnitCategory
    ? units.filter((u) => u.unit_category === recipeUnitCategory)
    : units
  ).map((u) => ({ label: u.unit_name, value: u.id }));

  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold" style={{ color: textColor }}>Variants</span>
        {/* 2026-09-02: Add hidden (view-only for now) — API still works, uncomment to restore.
        <Tooltip title={hideAddVariant ? 'Fixed from the product\'s own dosage form / variant selection' : (showForm ? 'Close' : 'Add Variant')}>
          {showForm ? (
            <Icons.X className="w-6 h-6 cursor-pointer text-red-500 hover:scale-110 transition-transform" onClick={resetForm} />
          ) : hideAddVariant ? (
            <Icons.Plus className="w-6 h-6 opacity-30 cursor-not-allowed" />
          ) : (
            <Icons.Plus className="w-6 h-6 cursor-pointer hover:scale-110 transition-transform" onClick={() => setShowForm(true)} />
          )}
        </Tooltip>
        */}
      </div>

      {showForm && (
        <div
          className="rounded-xl border mb-4"
          style={{
            backgroundColor: formBgColor,
            borderColor: accentColor,
            borderLeftWidth: 4,
            borderLeftStyle: 'solid',
            padding: '18px 20px 12px',
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-md" style={{ backgroundColor: accentBg }}>
              <Icons.Plus className="w-4 h-4" style={{ color: accentColor }} />
            </div>
            <span className="text-sm font-semibold" style={{ color: accentColor }}>
              {form.variant_id ? 'Edit Variant' : 'Add Variant'}
            </span>
          </div>
          <Row gutter={[12, 12]} align="top">
            <Col span={6}>
              <FieldLabel textColor={textColor}>Type</FieldLabel>
              <Select
                size="large"
                placeholder="Variant Type"
                style={{ width: '100%' }}
                value={form.variant_type_id || undefined}
                onChange={(val) => setForm({ ...form, variant_type_id: val })}
                options={variantTypes.map((t) => ({ label: t.name, value: t.variant_type_id }))}
              />
            </Col>
            <Col span={6}>
              <FieldLabel textColor={textColor}>Label (optional)</FieldLabel>
              <Input
                size="large"
                placeholder="e.g. 10mg strength"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Col>
            <Col span={4}>
              {/* 2026-09-02, "labels?": once a value is picked, a bare `placeholder=` disappears —
                  same gap as every other field here, but this one matters most since the whole
                  point of valueFieldLabel is to say WHAT the number means (Weight/Volume/Nos per
                  Unit), and that was only ever visible before the user typed anything. */}
              <FieldLabel textColor={textColor}>{valueFieldLabel}</FieldLabel>
              <InputNumber
                size="large"
                placeholder={valueFieldLabel}
                style={{ width: '100%' }}
                value={form.value}
                onChange={(val) => setForm({ ...form, value: val })}
              />
            </Col>
            <Col span={4}>
              <FieldLabel textColor={textColor}>UOM</FieldLabel>
              <Select
                size="large"
                placeholder="UOM"
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="label"
                value={form.uom_id || undefined}
                onChange={(val) => setForm({ ...form, uom_id: val })}
                options={valueUomOptions}
              />
            </Col>
            <Col span={4}>
              <FieldLabel textColor={textColor}>Output UOM</FieldLabel>
              <Select
                size="large"
                placeholder="Output UOM"
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="label"
                value={form.output_uom_id || undefined}
                onChange={(val) => setForm({ ...form, output_uom_id: val })}
                options={unitOptions}
              />
            </Col>
          </Row>
          <Row gutter={[12, 12]} align="top" className="mt-3">
            <Col span={6}>
              <FieldLabel textColor={textColor}>Output Quantity</FieldLabel>
              <InputNumber
                size="large"
                placeholder="blank = auto"
                style={{ width: '100%' }}
                value={form.output_quantity}
                onChange={(val) => setForm({ ...form, output_quantity: val ?? '' })}
              />
            </Col>
            <Col flex="auto" />
            <Col>
              <FieldLabel textColor={textColor}>&nbsp;</FieldLabel>
              <div className="flex gap-2">
                <Tooltip title="Reset">
                  <Button size="large" icon={<Icons.Undo2 className="w-4 h-4" />} onClick={resetForm} />
                </Tooltip>
                <Button size="large" type="primary" icon={<Icons.Plus className="w-4 h-4" />} loading={saving} onClick={handleSave}>
                  {form.variant_id ? 'Update' : 'Add'}
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      )}

      <TableContainer
        data={variants}
        columns={columns}
        size="small"
        bordered
        rowKey="variant_id"
        height={null}
        pagination={false}
        locale={{ emptyText: 'No variants — this MBMR has a single, fixed output' }}
      />

      <Modal
        open={viewerOpen}
        onCancel={() => setViewerOpen(false)}
        footer={null}
        width={900}
        title={
          <span>
            Composition per Unit
            {viewerVariant && (
              <span className="text-gray-400 font-normal">
                {' — '}{viewerVariant.name} ({viewerVariant.output_quantity ?? '—'} {viewerVariant.output_uom_name})
              </span>
            )}
          </span>
        }
      >
        {viewerVariant && !viewerVariant.output_quantity && (
          <div className="mb-3 text-sm" style={{ color: '#dc2626' }}>
            This variant has no Output Quantity set — per-unit values can&apos;t be calculated.
          </div>
        )}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm" style={{ color: textColor }}>Show per-unit values in:</span>
          <Select
            size="small"
            style={{ width: 140 }}
            value={displayUnitId || undefined}
            onChange={handleDisplayUnitChange}
            options={displayUnitOptions}
            showSearch
            optionFilterProp="label"
          />
        </div>
        <AntTable
          dataSource={viewerRows}
          columns={viewerColumns}
          rowKey="bom_item_id"
          size="small"
          bordered
          pagination={false}
          loading={viewerLoading}
          locale={{ emptyText: 'No BOM items on this recipe' }}
        />
      </Modal>
    </div>
  );
}
